import cn from 'classnames';
import { WEBSITE_URL } from 'core-lib/data/common';
import { useState } from 'react';
import { CalculationTypes } from 'src/modules/newCars/constants/model';
import { ModelDetailType, VariantsListType } from 'src/modules/newCars/types';
import { formatPrice } from 'src/modules/newCars/utils/helper';
import { processModelSeoData } from 'src/modules/newCars/utils/seoContent';
import { Button, Divider, Typography } from 'ui';
import { ButtonVariants, Color, TypeScales } from 'ui/primitives';
import LatestNews from '../../../LatestNews';
import VideoReview from '../../../VideoReview';
import { VariantListingCard } from '../../../cards/VariantListingCard/VariantListingCard';
import Faq from '../../../content/Faq';
import ScrollableJumbo from '../../../content/ScrollableJumbo';
import CalculatedValues from '../CalculatedValues';
import Detail from '../Detail';
import Header from '../Header';
import { UnderPriceCars } from '../UnderPriceCars/UnderPriceCars';
import UserReview from '../UserReview';
import Verdict from '../Verdict';
import s from './Mileage.module.scss';

function getUniqueMileage(list: VariantsListType[]) {
  const obj = {};
  for (let i = 0; i < list?.length; i++) {
    obj[list?.[i].mileage] = list[i];
  }
  return Object.values(obj);
}

const headerMileageData = {
  heading: '<brand-name> <model-name> Mileage',
  description:
    'Discover the impressive mileage of the <brand-name> <model-name> lineup. The base model of <brand-name> <model-name> delivers excellent fuel efficiency, providing a mileage of up to <base-model-mileage>*, making it an economical choice for your daily commutes. On the other hand, the top model of <brand-name> <model-name> showcases remarkable fuel efficiency, offering a mileage of up to <top-model-mileage>*, without compromising on performance and features. Experience the joy of driving while saving on fuel costs with the <brand-name> <model-name> range. Visit our showroom to explore the fuel-efficient variants available and embark on a fuel-efficient and eco-friendly journey with <brand-name> <model-name>.',
};

export const Mileage = ({
  content,
  strapiContent,
}: {
  content: ModelDetailType;
  strapiContent;
}) => {
  const uniqueMileage = getUniqueMileage(content?.variants);
  const [viewMore, setViewMore] = useState(false);
  return (
    <div className={s.root}>
      <div className={s.image_desc}>
        <Header images={content.vehicleImages} showBottom={false} content={content}/>
        <Detail
          data={content}
          averageRating={strapiContent?.calculatedReview?.ratingCount}
          showBottom={false}
          noOfRating={strapiContent?.calculatedReview?.ratingCount}
          price={`${formatPrice(
            content?.modelSeoData?.baseVariantPrice
          )} - ${formatPrice(content?.modelSeoData?.topVariantPrice)}`}
          brandSlug={content?.modelSeoData?.brandSlug}
          modelSlug={content?.modelSeoData?.modelSlug}
        />
      </div>
      <div className={s.divider}>
        <Divider variant="padded" />
      </div>
      <div className={s.header_data}>
        <Typography
          className={s.heading}
          label={processModelSeoData(headerMileageData.heading, content)}
          variant={TypeScales.h1}
          renderAs="H1"
        />
        <Typography
          label={processModelSeoData(headerMileageData.description, content)}
          variant={TypeScales.base_big_r}
          color={Color.text_subheading}
        />
      </div>
      <div className={s.table}>
        <Typography
          label={`Mileage Information`}
          variant={TypeScales.h3}
          className={s.header}
        />
        <table>
          <thead>
            <tr>
              <th>Fuel type</th>
              <th>Transmission</th>
              <th>ARAI Mileage</th>
            </tr>
          </thead>
          <tbody>
            {uniqueMileage
              ?.filter((obj: VariantsListType) => obj?.fuelType)
              ?.map((obj: VariantsListType) => (
                <tr key={obj?.mileage}>
                  <td>{obj.fuelType}</td>
                  <td>{obj.transmission}</td>
                  <td>{obj.mileage}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className={s.listing}>
        <div className={s.desc}>
          <Typography
            label={`${content?.hero?.brandName} ${content?.hero?.modelName} variants`}
            variant={TypeScales.h2}
            className={s.variant_head}
          />
        </div>
        <div className={cn(s.list, { [s.view_less]: !viewMore && content?.variants?.length > 3 })}>
          {content?.variants?.map((obj) => (
            <VariantListingCard
              variantDetail={obj}
              modelName={content?.hero?.modelName}
              modelImage={content?.hero?.image}
              key={obj?.variantKey}
              link={`${WEBSITE_URL}/new-cars/${
                content?.modelSeoData?.brandSlug
              }-car/${content?.modelSeoData?.modelSlug}/${obj?.slug?.replace(
                / /g,
                ''
              )}-variant`}
            />
          ))}
        </div>
        {content?.variants?.length > 3 && <Button
          variant={ButtonVariants.ButtonSecondary}
          label={`View ${viewMore ? 'less' : 'more'}`}
          onClick={() => setViewMore((state) => !state)}
          fullwidth
        />}
      </div>
      {content && (
        <UnderPriceCars
          content={content} 
        />
      )}

      {!content?.hero?.fuelType.includes("ev") && <div className={s.calc_values}>
        <CalculatedValues
          type={CalculationTypes.runningCost}
          variantList={content?.variants}
          brandName={content?.hero?.brandName}
          modelName={content?.hero?.modelName}
        />
      </div>}
      {strapiContent?.news?.length && (
        <LatestNews
          data={strapiContent?.news}
          heading={`${content?.hero?.brandName} ${content?.hero?.modelName} latest news`}
        />
      )}
      {strapiContent?.userReview && (
        <UserReview
          link={`/new-cars/${content?.modelSeoData?.brandSlug}-car/${content?.modelSeoData?.modelSlug}/review`}
          ratingData={strapiContent.userReview}
          calculatedReview={strapiContent.calculatedReview}
        />
      )}
      {strapiContent?.videoReview && (
        <VideoReview data={strapiContent.videoReview} />
      )}
      {strapiContent?.carListing?.mustRead && (
        <ScrollableJumbo
          heading="Must read article"
          description={strapiContent?.carListing?.mustRead}
        />
      )}
      {strapiContent?.verdict && (
        <Verdict verdictData={strapiContent.verdict} />
      )}
      {/* <BrandAd /> */}
      {strapiContent?.carListing?.faqTitle &&
        strapiContent?.carListing?.faqContent && (
          <div className={s.faq}>
            <Faq
              faqTitle={strapiContent?.carListing?.faqTitle}
              faqContent={processModelSeoData(strapiContent?.carListing?.faqContent,content)}
            />
          </div>
     
        )}
    </div>
  );
};
